import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = 'https://hoosierlifeinsurance.com';

export default function LifeInsuranceForBusinessOwnersIndiana() {
  useEffect(() => {
    document.title = 'Life Insurance for Business Owners in Indiana — Key Person, Buy-Sell & More';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Indiana business owners need more than personal life insurance. Learn about key person insurance, buy-sell agreements, and executive benefits that protect your company.');

    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'life insurance for business owners Indiana, key person insurance Indianapolis, buy-sell agreement life insurance Indiana');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/resources/life-insurance-for-business-owners-indiana`;

    const schemas = [
      {
        '@context': 'https://schema.org', '@type': 'Article',
        headline: 'Life Insurance for Business Owners in Indiana: Key Person, Buy-Sell & Executive Benefits',
        description: 'Indiana business owners face unique life insurance needs. This guide covers key person insurance, buy-sell agreements, and executive benefit strategies.',
        author: { '@type': 'Person', name: 'Benjamin Wright' },
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        datePublished: '2025-01-12T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/resources/life-insurance-for-business-owners-indiana`,
      },
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Resources', item: `${SITE_URL}/resources` },
          { '@type': 'ListItem', position: 3, name: 'Life Insurance for Business Owners in Indiana', item: `${SITE_URL}/resources/life-insurance-for-business-owners-indiana` },
        ],
      },
      {
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: [
          { '@type': 'Question', name: 'Is key person life insurance tax-deductible for Indiana businesses?', acceptedAnswer: { '@type': 'Answer', text: 'Key person life insurance premiums are generally NOT tax-deductible when the business is the beneficiary. However, the death benefit is received tax-free. For buy-sell agreement policies, the tax treatment depends on the structure (cross-purchase vs. entity purchase). Consult a CPA for your specific situation.' } },
          { '@type': 'Question', name: 'How much key person insurance does my Indiana business need?', acceptedAnswer: { '@type': 'Answer', text: 'A common formula is 5-10x the key person\'s annual compensation, or the estimated cost to recruit, hire, and train a replacement plus lost revenue during the transition. For a business owner, it may also include the value of their ownership stake. Most Indiana small businesses carry $500K-$2M in key person coverage.' } },
          { '@type': 'Question', name: 'What happens to my life insurance if I sell my Indiana business?', acceptedAnswer: { '@type': 'Answer', text: 'If you sell your business, you can typically transfer ownership of any personally-owned policies to yourself. Business-owned policies (key person, buy-sell) may be transferred to the new owner, surrendered for cash value, or converted. The tax implications vary — work with a business attorney and CPA before any sale.' } },
        ],
      },
    ];

    schemas.forEach((schema, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-bizowner-${i}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    return () => { schemas.forEach((_, i) => document.getElementById(`schema-bizowner-${i}`)?.remove()); };
  }, []);

  const coverageTypes = [
    {
      icon: 'ri-user-star-line', title: 'Key Person Insurance', color: '#D97642',
      who: 'Business pays premiums, business is beneficiary',
      purpose: 'Protects the company if a critical employee or owner dies unexpectedly',
      amount: '5-10x key person\'s annual salary',
      bestFor: 'Any business with employees whose loss would cause significant revenue disruption',
      example: 'A Carmel software firm insures their lead developer for $1M. If he dies, the company uses the payout to hire a replacement and cover lost contracts during the transition.',
    },
    {
      icon: 'ri-shake-hands-line', title: 'Buy-Sell Agreement Insurance', color: '#1A3A52',
      who: 'Partners insure each other (cross-purchase) or business insures partners (entity)',
      purpose: 'Funds the buyout of a deceased partner\'s ownership stake',
      amount: 'Equal to each partner\'s ownership value',
      bestFor: 'Multi-owner businesses, partnerships, LLCs with 2+ members',
      example: 'Two Indianapolis restaurant owners each carry $800K policies on each other. If one dies, the survivor uses the death benefit to buy out the deceased\'s family — keeping the business intact.',
    },
    {
      icon: 'ri-briefcase-4-line', title: 'Executive Bonus Plan (Section 162)', color: '#2D6A4F',
      who: 'Business pays premiums as a bonus to the executive',
      purpose: 'Attracts and retains key executives with tax-advantaged life insurance benefits',
      amount: 'Varies — typically $100K-$1M whole life policies',
      bestFor: 'Businesses wanting to reward key executives with tax-deductible compensation',
      example: 'A Fishers manufacturing company pays $15,000/year in life insurance premiums for their VP of Sales as a bonus. The company deducts it; the executive owns the policy.',
    },
    {
      icon: 'ri-bank-line', title: 'COLI (Corporate-Owned Life Insurance)', color: '#5C4033',
      who: 'Business owns and is beneficiary of policies on multiple employees',
      purpose: 'Tax-advantaged asset on the balance sheet; funds employee benefit obligations',
      amount: 'Varies widely — often $500K-$5M per covered employee',
      bestFor: 'Larger Indiana businesses (50+ employees) funding pension or benefit obligations',
      example: 'An Indianapolis bank carries COLI policies on 50 senior employees. The cash value grows tax-deferred and funds the bank\'s deferred compensation obligations.',
    },
  ];

  return (
    <>
      <title>Life Insurance for Business Owners in Indiana — Key Person, Buy-Sell &amp; More | Hoosier Life Insurance</title>
      <article className="min-h-screen bg-white">
        <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white py-20">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="mb-6">
              <Link to="/resources" className="inline-flex items-center text-white/70 hover:text-white transition-colors text-sm">
                <i className="ri-arrow-left-line mr-2" />Back to Resources
              </Link>
            </div>
            <div className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#D97642] text-sm font-semibold px-4 py-2 rounded-full mb-6">
              <i className="ri-briefcase-line" />Business Planning
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Life Insurance for Indiana Business Owners — Key Person, Buy-Sell &amp; Executive Benefits</h1>
            <p className="text-xl text-white/80 mb-8">Key person coverage, buy-sell agreements, and executive benefits — the complete guide for Indiana entrepreneurs and business owners.</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span><i className="ri-calendar-line mr-2" />Updated January 2026</span>
              <span><i className="ri-time-line mr-2" />11 min read</span>
              <span><i className="ri-map-pin-line mr-2" />Indiana Business Owners</span>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              As an Indiana business owner, you have two separate life insurance needs: <strong>personal coverage</strong> for your family, and <strong>business coverage</strong> to protect the company you've built. Most business owners only think about the first one.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This guide covers the four types of business life insurance every Indiana entrepreneur should understand — and how to structure them to protect both your family and your company.
            </p>
          </div>

          <div className="bg-[#FDF6F0] border-l-4 border-[#D97642] p-6 mb-12 rounded-r-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
              <i className="ri-lightbulb-line text-[#D97642] mr-3 text-2xl" />The Business Owner's Life Insurance Checklist
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Personal term life (income replacement for family)',
                'Key person insurance (protects the business)',
                'Buy-sell agreement funding (if you have partners)',
                'Executive bonus plan (retain key employees)',
              ].map(item => (
                <div key={item} className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-[#D97642] mt-1 flex-shrink-0" />
                  <span className="text-gray-800 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Coverage Types */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">4 Types of Business Life Insurance for Indiana Owners</h2>
            <div className="space-y-8">
              {coverageTypes.map((type, i) => (
                <div key={type.title} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#D97642] transition-colors">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 text-white" style={{ backgroundColor: type.color }}>
                      <i className={`${type.icon} text-2xl`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">Type {i + 1}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{type.title}</h3>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Who Pays / Who Benefits</p>
                      <p className="text-gray-700 text-sm">{type.who}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Purpose</p>
                      <p className="text-gray-700 text-sm">{type.purpose}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Typical Coverage Amount</p>
                      <p className="text-gray-700 text-sm font-semibold">{type.amount}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Best For</p>
                      <p className="text-gray-700 text-sm">{type.bestFor}</p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-5">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Indiana Example</p>
                    <p className="text-gray-700 text-sm italic">{type.example}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Buy-Sell Deep Dive */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Buy-Sell Agreements: Cross-Purchase vs. Entity Purchase</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-[#D97642] rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Cross-Purchase Agreement</h3>
                <p className="text-gray-700 text-sm mb-4">Each partner buys a policy on every other partner. If Partner A dies, Partner B uses the death benefit to buy A's shares from A's estate.</p>
                <div className="space-y-2 mb-4">
                  <p className="text-xs font-bold text-gray-400 uppercase">Pros</p>
                  {['Surviving partners get stepped-up cost basis', 'Simpler for 2-partner businesses', 'Partners own their policies personally'].map(p => (
                    <div key={p} className="flex items-start gap-2 text-sm text-gray-700"><i className="ri-check-line text-green-600 mt-1 flex-shrink-0" />{p}</div>
                  ))}
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-bold text-gray-400 uppercase">Cons</p>
                  {['Gets complex with 3+ partners (N×(N-1) policies needed)', 'Premiums paid with after-tax dollars'].map(p => (
                    <div key={p} className="flex items-start gap-2 text-sm text-gray-700"><i className="ri-close-line text-red-500 mt-1 flex-shrink-0" />{p}</div>
                  ))}
                </div>
              </div>
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Entity Purchase Agreement</h3>
                <p className="text-gray-700 text-sm mb-4">The business buys policies on each partner. If a partner dies, the business uses the death benefit to buy back the deceased's shares.</p>
                <div className="space-y-2 mb-4">
                  <p className="text-xs font-bold text-gray-400 uppercase">Pros</p>
                  {['Simpler with 3+ partners (one policy per partner)', 'Business pays premiums (may be more affordable)', 'Easier administration'].map(p => (
                    <div key={p} className="flex items-start gap-2 text-sm text-gray-700"><i className="ri-check-line text-green-600 mt-1 flex-shrink-0" />{p}</div>
                  ))}
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-bold text-gray-400 uppercase">Cons</p>
                  {['No stepped-up cost basis for surviving partners', 'Potential AMT issues for C-corps'].map(p => (
                    <div key={p} className="flex items-start gap-2 text-sm text-gray-700"><i className="ri-close-line text-red-500 mt-1 flex-shrink-0" />{p}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* What Happens Without Coverage */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What Happens to Your Indiana Business Without Coverage?</h2>
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 bg-red-600 text-white rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-alarm-warning-line text-2xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">The Unprotected Business Owner Scenario</h3>
                  <p className="text-gray-700">Here's what typically happens when an Indiana business owner dies without proper coverage in place:</p>
                </div>
              </div>
              <div className="space-y-4">
                {[
                  { step: 'Week 1', event: 'Business operations disrupted', detail: 'Key decisions can\'t be made. Clients and vendors are uncertain. Revenue drops immediately.' },
                  { step: 'Month 1', event: 'Partners or heirs in conflict', detail: 'Without a buy-sell agreement, the deceased\'s family may become unwanted business partners — or force a sale at the worst possible time.' },
                  { step: 'Month 3', event: 'Business may need to be sold', detail: 'Without key person insurance to fund operations, the business may be forced into a distressed sale at 30-50 cents on the dollar.' },
                  { step: 'Month 6', event: 'Family loses business value', detail: 'The business the owner spent decades building may be worth a fraction of its true value — or gone entirely.' },
                ].map(s => (
                  <div key={s.step} className="bg-white rounded-xl p-5 flex items-start gap-4">
                    <div className="w-20 flex-shrink-0">
                      <span className="text-xs font-bold text-red-600 uppercase">{s.step}</span>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 mb-1">{s.event}</p>
                      <p className="text-sm text-gray-600">{s.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Common Questions from Indiana Business Owners</h2>
            <div className="space-y-4">
              {[
                { q: 'Is key person life insurance tax-deductible for Indiana businesses?', a: 'Key person premiums are generally NOT tax-deductible when the business is the beneficiary. However, the death benefit is received tax-free. For executive bonus plans (Section 162), premiums are deductible as compensation. Consult a CPA for your specific situation.' },
                { q: 'How much key person insurance does my Indiana business need?', a: 'A common formula is 5-10x the key person\'s annual compensation, or the estimated cost to recruit and train a replacement plus lost revenue during the transition. Most Indiana small businesses carry $500K-$2M in key person coverage.' },
                { q: 'What happens to my life insurance if I sell my Indiana business?', a: 'Personally-owned policies transfer with you. Business-owned policies (key person, buy-sell) may be transferred to the new owner, surrendered for cash value, or converted. Work with a business attorney and CPA before any sale.' },
              ].map(faq => (
                <div key={faq.q} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-question-line text-[#D97642] mr-3 text-2xl" />{faq.q}
                  </h3>
                  <p className="text-gray-700 text-sm pl-11">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-4">Protect Your Indiana Business Today</h2>
            <p className="text-xl text-white/80 mb-8">Benjamin Wright specializes in business life insurance for Indiana entrepreneurs. Let's build a complete protection strategy for your company.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/book-appointment" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-50 transition-colors whitespace-nowrap">Schedule a Business Review</Link>
              <Link to="/business-life-insurance-indianapolis" className="bg-[#0F2A3F] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0A1F2E] transition-colors border-2 border-white/30 whitespace-nowrap">Business Insurance Page</Link>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { to: '/business-life-insurance-indianapolis', icon: 'ri-building-line', title: 'Business Life Insurance Indianapolis', desc: 'Key person, buy-sell, and executive benefit solutions for Indianapolis businesses' },
                { to: '/resources/how-much-life-insurance-indiana-families-need', icon: 'ri-calculator-line', title: 'How Much Coverage Do You Need?', desc: 'Calculate the right personal coverage amount for your Indiana family' },
                { to: '/resources/term-vs-whole-life-indianapolis-homeowners', icon: 'ri-scales-line', title: 'Term vs. Whole Life Insurance', desc: 'Which policy type is right for Indiana business owners?' },
              ].map(r => (
                <Link key={r.to} to={r.to} className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                  <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className={`${r.icon} text-2xl`} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">{r.title}</h3>
                  <p className="text-sm text-gray-600">{r.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Protect Your Indiana Business?</h2>
            <p className="text-xl text-gray-700 mb-8">Our agents specialize in business life insurance for Indiana entrepreneurs. Let's build a complete protection strategy.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3176482983" className="bg-[#1A3A52] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#0F2A3F] transition-colors whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-phone-line mr-2" />Call (317) 648-2983
              </a>
              <Link to="/contact" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors border-2 border-[#1A3A52] whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-message-3-line mr-2" />Send a Message
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
